import dayjs from "dayjs";

import { TimeZoneEnum } from "@ryde/shared";
import { blueMui, greenMui, greyMui, purpleMui } from "@ryde/theme";

const MIDNIGHT_CROSSING_MAX_REASONABLE_DIFF_HOURS = 10;

/**
 * The time-only picker (MUI TimeField) with a timezone prop converts values
 * to local time for display and editing. When a station's arrival is past
 * local midnight (e.g. 00:05 local on March 25, stored as March 24 22:05 UTC),
 * the picker's internal local date is the next day. If the user then types a
 * pre-midnight local time (e.g. 23:45), the picker produces March 25 23:45
 * local → March 25 21:45 UTC, instead of the intended March 24 21:45 UTC.
 * Detects this by checking if the diff exceeds a threshold and subtracts a day.
 */
export const adjustTimeForMidnightCrossingRide = ({
  newTimeISO,
  previousTimeISO,
  rideStart,
  rideEnd,
  timezone,
}: {
  newTimeISO: TDateISO;
  previousTimeISO: TDateISO;
  rideStart: TDateISO | undefined;
  rideEnd: TDateISO | undefined;
  timezone: TimeZoneEnum;
}): TDateISO => {
  if (!rideStart || !rideEnd) {
    return newTimeISO;
  }

  const rideStartLocal = dayjs.utc(rideStart).tz(timezone);
  const rideEndLocal = dayjs.utc(rideEnd).tz(timezone);
  const isRideCrossingLocalMidnight = !rideStartLocal.isSame(
    rideEndLocal,
    "day",
  );

  const diffHours = Math.abs(
    dayjs.utc(newTimeISO).diff(dayjs.utc(previousTimeISO), "hour"),
  );

  if (
    !isRideCrossingLocalMidnight &&
    diffHours >= MIDNIGHT_CROSSING_MAX_REASONABLE_DIFF_HOURS
  ) {
    return dayjs.utc(newTimeISO).add(1, "day").toISOString();
  }

  if (diffHours >= MIDNIGHT_CROSSING_MAX_REASONABLE_DIFF_HOURS) {
    return dayjs.utc(newTimeISO).subtract(1, "day").toISOString();
  }

  return newTimeISO;
};

export const getShiftTimeColor = ({
  bookingId,
  shiftHour,
  shiftHourByArrival,
  isJoined,
}: {
  bookingId: number | undefined;
  shiftHour: TDateISO | undefined;
  shiftHourByArrival: TDateISO | undefined;
  isJoined: boolean | undefined;
}): string => {
  const hasShiftHour = !!shiftHour;
  const hasShiftTimeByArrival = !!shiftHourByArrival;

  if (!bookingId && shiftHour && !isJoined) {
    // passenger was added by produce, but booking id swiped by syncer
    return greyMui._500;
  }

  if (isJoined) {
    // If for joined passenger we have shiftTimeByArrival but no shiftHour, use dark green
    if (!hasShiftHour && hasShiftTimeByArrival) {
      return greenMui._900;
    }

    // passenger was joined to the ride by shiftTime
    return blueMui._700;
  } else {
    if (!hasShiftHour && hasShiftTimeByArrival) {
      // passenger was using manual arrival time instead of shift time while timing
      return purpleMui._700;
    }
  }

  return greyMui._700;
};
